import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { BIOMES, currentBiomeAt } from '../biomes'
import { pickMicrobeSpecies, recipeForSpecies } from '../species'
import { morphologyFrom, behaviorForBody } from '../creature-recipe'
import { rollFoodRecipe } from '../creature-procgen'
import { grazeAt } from '../graze'
import { inWater } from '../dungeon/depth-field'
import { maybeSpawnColony, maybeSpawnNetwork } from './colony'
import { maybeSpawnPuffer } from './spore-puffer'
import { T } from '../tunables'

export function spawnSystem(world: GameWorld, player: Entity): void {
  // Despawn microbes that have drifted far behind the player. Colony members
  // are managed by colonySystem (whole colony despawns together), so skip them.
  const limit2 = T.spawn.microbeDespawnDistance * T.spawn.microbeDespawnDistance
  const stale: Entity[] = []
  for (const e of world.with('consumable', 'position')) {
    if (e.absorbing || e.recruitable || e.colonyMember) continue
    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    if (dx * dx + dy * dy > limit2) stale.push(e)
  }
  for (const e of stale) {
    if (world.id(e) !== undefined) world.remove(e)
  }

  // Occasionally seed a colony; its members count toward the microbe target.
  maybeSpawnColony(world, player)
  // Spore-puffers populate the mushroom floor (gated inside).
  maybeSpawnPuffer(world, player)
  // A drifting mycelial network — a mushroom-floor set-piece (gated inside).
  maybeSpawnNetwork(world, player)

  const living = [...world.with('consumable')].filter(e => !e.absorbing)
  const deficit = T.spawn.microbeTarget - living.length
  if (deficit <= 0) return

  for (let i = 0; i < deficit; i++) {
    // Walled world: the spawn ring clips into banks, so take a few samples
    // before giving this slot up (the deficit persists to the next frame —
    // tight corridors staying leaner on food is the intended gradient).
    let x = player.position.x
    let y = player.position.y
    let wet = false
    for (let attempt = 0; attempt < 4 && !wet; attempt++) {
      const angle = Math.random() * Math.PI * 2
      const r = T.spawn.microbeSpawnRingMin + Math.random() * (T.spawn.microbeSpawnRingMax - T.spawn.microbeSpawnRingMin)
      x = player.position.x + Math.cos(angle) * r
      y = player.position.y + Math.sin(angle) * r
      wet = inWater(x, y, 0.12)
    }
    if (!wet) continue
    // Overgrazing: a stripped cell mostly refuses respawns. The deficit
    // persists, so food stays scarce here until the field regrows or the
    // player moves somewhere greener.
    if (Math.random() < grazeAt(x, y)) continue
    const variant = currentBiomeAt(x, y)
    const tint = BIOMES[variant].microbeTint()
    // A fraction of food is procedurally rolled from the biome palette; the
    // rest are the curated named species. Behavior follows the body plan either
    // way (a rolled rod darts like the named one).
    const recipe = Math.random() < T.spawn.proceduralChance
      ? rollFoodRecipe(variant)
      : recipeForSpecies(pickMicrobeSpecies(variant))
    const behavior = behaviorForBody(recipe.body)
    // Vaults (plant cells) are bigger — a visibly juicier target.
    const radius = behavior === 'vault'
      ? T.creatures.vault.radiusMin + Math.random() * (T.creatures.vault.radiusMax - T.creatures.vault.radiusMin)
      : 0.05 + Math.random() * 0.04
    const entity: Entity = {
      position: { x, y },
      velocity: { x: 0, y: 0 },
      wobble: { phase: Math.random() * Math.PI * 2, freq: 1.5 + Math.random() * 1.5, amp: 0.05 },
      renderable: { shape: 'microbe', radius, tint, variant },
      morphology: morphologyFrom(recipe, Math.random()),
      consumable: true,
    }
    // A creature carries EITHER behavior OR wander, never both.
    if (behavior) {
      entity.behavior = {
        kind: behavior,
        noiseSeed: Math.random() * 100,
        burstTimer: Math.random() * 0.5,
        heading: Math.random() * Math.PI * 2,
      }
    } else {
      entity.wander = { impulseTimer: Math.random() * 2, noiseSeed: Math.random() * 100 }
    }
    world.add(entity)
  }
}
